import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Chip,
  CircularProgress,
  Card,
  CardContent,
  Grid,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Upload as UploadIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';

interface Font {
  id: number;
  font_name: string;
  file_name: string;
  font_family: string;
  font_weight: string;
  font_style: string;
  bank_name: string | null;
  description: string | null;
  created_at: string;
}

interface RequiredFont {
  font_name: string;
  clean_name: string;
  usage_count: number;
  available: boolean;
}

const FontManagement: React.FC = () => {
  const [fonts, setFonts] = useState<Font[]>([]);
  const [requiredFonts, setRequiredFonts] = useState<RequiredFont[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadData, setUploadData] = useState({
    fontName: '',
    fontFamily: '',
    fontWeight: 'normal',
    fontStyle: 'normal',
    bankName: '',
    description: '',
  });
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const fetchFonts = async () => {
    try {
      const [fontsRes, requiredRes] = await Promise.all([
        fetch('/api/fonts'),
        fetch('/api/fonts/required'),
      ]);

      const fontsData = await fontsRes.json();
      const requiredData = await requiredRes.json();

      if (fontsData.success) setFonts(fontsData.fonts);
      if (requiredData.success) setRequiredFonts(requiredData.fonts);
    } catch (err) {
      setError('Failed to fetch fonts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFonts();
  }, []);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      setUploadFile(file);
      const name = file.name.replace(/\.(ttf|otf|woff|woff2)$/i, '');
      setUploadData(prev => ({ ...prev, fontName: name, fontFamily: name }));
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'font/ttf': ['.ttf'],
      'font/otf': ['.otf'],
      'font/woff': ['.woff'],
      'font/woff2': ['.woff2'],
    },
    maxFiles: 1,
  });

  const handleUpload = async () => {
    if (!uploadFile || !uploadData.fontName) {
      setError('Please select a file and provide a font name');
      return;
    }

    const formData = new FormData();
    formData.append('font', uploadFile);
    formData.append('fontName', uploadData.fontName);
    formData.append('fontFamily', uploadData.fontFamily || uploadData.fontName);
    formData.append('fontWeight', uploadData.fontWeight);
    formData.append('fontStyle', uploadData.fontStyle);
    if (uploadData.bankName) formData.append('bankName', uploadData.bankName);
    if (uploadData.description) formData.append('description', uploadData.description);

    try {
      const res = await fetch('/api/fonts/upload', {
        method: 'POST',
        body: formData,
      });

      const data = await res.json();

      if (data.success) {
        setSuccess('Font uploaded successfully');
        setUploadOpen(false);
        setUploadFile(null);
        setUploadData({
          fontName: '',
          fontFamily: '',
          fontWeight: 'normal',
          fontStyle: 'normal',
          bankName: '',
          description: '',
        });
        fetchFonts();
      } else {
        setError(data.error);
      }
    } catch (err) {
      setError('Failed to upload font');
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this font?')) return;

    try {
      const res = await fetch(`/api/fonts/${id}`, { method: 'DELETE' });
      const data = await res.json();

      if (data.success) {
        setSuccess('Font deleted successfully');
        fetchFonts();
      } else {
        setError(data.error);
      }
    } catch (err) {
      setError('Failed to delete font');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>
        Font Management
      </Typography>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Required Fonts (from PDF templates)
          </Typography>
          <Typography variant="body2" color="textSecondary" paragraph>
            These fonts were detected in your uploaded PDF templates. Upload matching fonts for accurate text replacement.
          </Typography>

          <Grid container spacing={1}>
            {requiredFonts.map((font) => (
              <Grid item key={font.font_name}>
                <Chip
                  icon={font.available ? <CheckIcon /> : <WarningIcon />}
                  label={`${font.clean_name} (${font.usage_count})`}
                  color={font.available ? 'success' : 'warning'}
                  variant={font.available ? 'filled' : 'outlined'}
                />
              </Grid>
            ))}
          </Grid>

          <Box mt={2}>
            <Alert severity="info">
              <strong>Bank of America "Connections" font:</strong> This is a proprietary font not available for purchase.
              <br />
              <strong>Alternatives:</strong> Avenir (~$35), Proxima Nova (~$29), Nunito Sans (FREE), Source Sans Pro (FREE)
            </Alert>
          </Box>
        </CardContent>
      </Card>

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Uploaded Fonts</Typography>
        <Button
          variant="contained"
          startIcon={<UploadIcon />}
          onClick={() => setUploadOpen(true)}
        >
          Upload Font
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Font Name</TableCell>
              <TableCell>Family</TableCell>
              <TableCell>Weight</TableCell>
              <TableCell>Style</TableCell>
              <TableCell>Bank</TableCell>
              <TableCell>File</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {fonts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  No fonts uploaded yet. Click "Upload Font" to add fonts.
                </TableCell>
              </TableRow>
            ) : (
              fonts.map((font) => (
                <TableRow key={font.id}>
                  <TableCell>{font.font_name}</TableCell>
                  <TableCell>{font.font_family}</TableCell>
                  <TableCell>{font.font_weight}</TableCell>
                  <TableCell>{font.font_style}</TableCell>
                  <TableCell>{font.bank_name || '-'}</TableCell>
                  <TableCell>{font.file_name}</TableCell>
                  <TableCell>
                    <IconButton color="error" onClick={() => handleDelete(font.id)}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={uploadOpen} onClose={() => setUploadOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Upload Font</DialogTitle>
        <DialogContent>
          <Box
            {...getRootProps()}
            sx={{
              border: '2px dashed',
              borderColor: isDragActive ? 'primary.main' : 'grey.400',
              borderRadius: 2,
              p: 3,
              textAlign: 'center',
              cursor: 'pointer',
              mb: 2,
              mt: 1,
              bgcolor: isDragActive ? 'action.hover' : 'transparent',
            }}
          >
            <input {...getInputProps()} />
            {uploadFile ? (
              <Typography>{uploadFile.name}</Typography>
            ) : (
              <Typography color="textSecondary">
                Drag & drop a font file here, or click to select
                <br />
                <small>Supported: TTF, OTF, WOFF, WOFF2</small>
              </Typography>
            )}
          </Box>

          <TextField
            fullWidth
            label="Font Name *"
            value={uploadData.fontName}
            onChange={(e) => setUploadData({ ...uploadData, fontName: e.target.value })}
            margin="normal"
            helperText="This name will be used to reference the font in templates"
          />

          <TextField
            fullWidth
            label="Font Family"
            value={uploadData.fontFamily}
            onChange={(e) => setUploadData({ ...uploadData, fontFamily: e.target.value })}
            margin="normal"
          />

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Weight</InputLabel>
                <Select
                  value={uploadData.fontWeight}
                  onChange={(e) => setUploadData({ ...uploadData, fontWeight: e.target.value })}
                  label="Weight"
                >
                  <MenuItem value="normal">Normal</MenuItem>
                  <MenuItem value="bold">Bold</MenuItem>
                  <MenuItem value="light">Light</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Style</InputLabel>
                <Select
                  value={uploadData.fontStyle}
                  onChange={(e) => setUploadData({ ...uploadData, fontStyle: e.target.value })}
                  label="Style"
                >
                  <MenuItem value="normal">Normal</MenuItem>
                  <MenuItem value="italic">Italic</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <TextField
            fullWidth
            label="Bank Name"
            value={uploadData.bankName}
            onChange={(e) => setUploadData({ ...uploadData, bankName: e.target.value })}
            margin="normal"
            helperText="Associate this font with a specific bank (e.g., Bank of America)"
          />

          <TextField
            fullWidth
            label="Description"
            value={uploadData.description}
            onChange={(e) => setUploadData({ ...uploadData, description: e.target.value })}
            margin="normal"
            multiline
            rows={2}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadOpen(false)}>Cancel</Button>
          <Button onClick={handleUpload} variant="contained" disabled={!uploadFile}>
            Upload
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FontManagement;
